import { Controller } from "@hotwired/stimulus"
import { loadPlan, savePlan } from "plan_storage"
import { renderIcons } from "lucide_icons"

// Right-side "Quick add references" drawer. Mounted once on the Goals section.
// Clicking a goal's Reference Documents area (or its "Quick add from library"
// pill) slides a fixed panel in from the right listing the workspace's
// reference library (the presets injected as window.PCPM_REFERENCE_DOCS from
// Settings). Clicking a chip adds that reference to the goal whose section was
// clicked, straight to the plan + a plan:change broadcast — so the goal's
// goal-references controller re-renders with the new row (and its ✓ state).
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
}[c]))

function presets() {
  const injected = (typeof window !== "undefined" && Array.isArray(window.PCPM_REFERENCE_DOCS))
    ? window.PCPM_REFERENCE_DOCS.filter(p => p && p.chip && p.title)
    : null
  return injected || []
}

export default class extends Controller {
  connect() {
    this._activeGoalId = null
    this._buildPanel()
    this._onClick   = (e) => this._onSectionClick(e)
    this._onKey     = (e) => { if (e.key === "Escape") this._hide() }
    this._onDocDown = (e) => {
      if (!this._panel || this._panel.hidden) return
      if (this._panel.contains(e.target)) return
      if (this._fromReferenceSection(e.target)) return   // switching to another goal
      this._hide()
    }
    this.element.addEventListener("click", this._onClick)
    document.addEventListener("keydown", this._onKey)
    document.addEventListener("mousedown", this._onDocDown, true)
  }

  disconnect() {
    this.element.removeEventListener("click", this._onClick)
    document.removeEventListener("keydown", this._onKey)
    document.removeEventListener("mousedown", this._onDocDown, true)
    if (this._panel) this._panel.remove()
  }

  _fromReferenceSection(t) {
    return !!(t && t.closest && t.closest('[data-controller~="goal-references"]'))
  }

  // Open when the "Quick add" pill is clicked, or when a reference section is
  // clicked anywhere that ISN'T an interactive control (inputs / buttons keep
  // their own behaviour so editing a reference doesn't pop the panel).
  _onSectionClick(e) {
    const t = e.target
    const trigger = t.closest && t.closest("[data-reference-quickadd]")
    const section = this._fromReferenceSection(t)
    if (!trigger && !section) return
    if (!trigger && t.closest && t.closest("input, textarea, button, a, select")) return
    const row = t.closest && t.closest("[data-row]")
    const goalId = row && row.dataset && row.dataset.id
    if (!goalId) return
    this._activeGoalId = goalId
    this._render()
    this._show()
  }

  _buildPanel() {
    if (this._panel) return
    const p = document.createElement("aside")
    p.className = "en-refpanel"
    p.hidden = true
    p.setAttribute("role", "dialog")
    p.setAttribute("aria-label", "Quick add references")
    p.innerHTML =
      '<div class="en-refpanel-head">' +
        '<span class="en-refpanel-title"><i data-lucide="book-marked"></i> Quick add references</span>' +
        '<button type="button" class="en-refpanel-close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="en-refpanel-body"></div>'
    p.querySelector(".en-refpanel-close").addEventListener("click", () => this._hide())
    // Live inside the existing right split pane, not as a new floating drawer.
    const host = document.querySelector(".en-wiz-pane") || document.body
    host.appendChild(p)
    this._panel = p
  }

  _render() {
    const body = this._panel.querySelector(".en-refpanel-body")
    const list = presets()
    if (!list.length) {
      body.innerHTML =
        '<p class="en-refpanel-empty">No references in your library yet. Add some in ' +
        '<strong>Settings → Reference documents</strong>, or save one straight from a goal.</p>'
      renderIcons(this._panel)
      return
    }
    const onGoal = new Set(this._activeGoalTitles())
    body.innerHTML = list.map((p, i) => {
      const added = onGoal.has((p.title || "").trim())
      return `
        <button type="button" class="en-refpanel-chip${added ? " is-added" : ""}"
                data-ref-idx="${i}" ${added ? "disabled" : ""} title="${esc(p.title)}">
          <span class="en-refpanel-chip-icon"><i data-lucide="${added ? "check" : "plus"}"></i></span>
          <span class="en-refpanel-chip-label">${esc(p.chip)}</span>
        </button>`
    }).join("")
    body.querySelectorAll(".en-refpanel-chip:not(.is-added)").forEach(btn => {
      btn.addEventListener("click", () => this._add(parseInt(btn.dataset.refIdx, 10)))
    })
    renderIcons(this._panel)
  }

  _activeGoalTitles() {
    const g = this._goal()
    return (g && Array.isArray(g.references)) ? g.references.map(r => (r.title || "").trim()) : []
  }

  _goal() {
    const plan = this._load()
    return (plan.goals || []).find(g => String(g.id) === String(this._activeGoalId)) || null
  }

  _add(idx) {
    const p = presets()[idx]
    if (!p || !this._activeGoalId) return
    const plan = this._load()
    const goal = (plan.goals || []).find(g => String(g.id) === String(this._activeGoalId))
    if (!goal) return
    if (!Array.isArray(goal.references)) goal.references = []
    const title = (p.title || "").trim()
    if (goal.references.some(r => (r.title || "").trim() === title)) { this._render(); return }
    goal.references.push({ id: Date.now() + Math.random(), title: p.title, url: p.url })
    savePlan(plan)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "goals", plan, source: "reference-panel", nested: true }
    }))
    this._render()   // repaint: the added chip flips to a ✓ and disables
  }

  _show() {
    // Make sure the right split pane is expanded so the panel is visible.
    const app = document.querySelector(".en-app")
    if (app) app.classList.remove("is-wiz-hidden")
    this._panel.hidden = false
    requestAnimationFrame(() => this._panel.classList.add("is-open"))
  }

  _hide() {
    if (!this._panel) return
    this._panel.classList.remove("is-open")
    this._panel.hidden = true
  }

  _load() { try { return loadPlan() || {} } catch (_) { return {} } }
}
